import { memo } from 'react';
import { FileCode, FileText, File, X, AlertCircle, RotateCcw } from 'lucide-react';
import CodeEditor from './CodeEditor';
import type { EditorTab, RenderResult } from '../types';
import clsx from 'clsx';

interface EditorPanelProps {
  className?: string;
  editorTabs: EditorTab[];
  activeTab: string;
  setActiveTab: (id: string) => void;
  removeEditorTab: (id: string) => void;
  currentContent: string;
  onEditorChange?: (value: string) => void;
  isReadOnly: boolean;
  isMobile: boolean;
  renderResult: RenderResult | null;
  onResetValues?: () => void;
  canResetValues?: boolean;
}

function EditorPanelComponent({
  className,
  editorTabs,
  activeTab,
  setActiveTab,
  removeEditorTab,
  currentContent,
  onEditorChange,
  isReadOnly,
  isMobile,
  renderResult,
  onResetValues,
  canResetValues,
}: EditorPanelProps) {
  return (
    <div className={clsx("h-full flex flex-col bg-dark-900", className)}>
      {/* Tabs */}
      <div className="flex items-center border-b border-dark-700 bg-dark-850">
        <div className="flex items-center flex-1 min-w-0 overflow-x-auto scrollbar-hide-mobile">
          {editorTabs.map((tab) => (
            <div
              key={tab.id}
              className={clsx(
                'flex items-center gap-1.5 px-3 md:px-2.5 py-2 md:py-1.5 border-r border-dark-700 cursor-pointer min-w-0',
                activeTab === tab.id
                  ? 'bg-dark-900 text-dark-100'
                  : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800'
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.type === 'values' ? (
                <FileText className="w-3.5 h-3.5 md:w-3 md:h-3 text-yellow-500 flex-shrink-0" />
              ) : tab.type === 'template' ? (
                <FileCode className="w-3.5 h-3.5 md:w-3 md:h-3 text-blue-500 flex-shrink-0" />
              ) : (
                <File className="w-3.5 h-3.5 md:w-3 md:h-3 text-green-500 flex-shrink-0" />
              )}
              <span className="text-sm md:text-xs truncate max-w-[100px] md:max-w-[120px]">{tab.name}</span>
              {tab.readOnly && (
                <span className="text-[10px] bg-dark-600 px-1 rounded">ro</span>
              )}
              {tab.id !== 'values' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEditorTab(tab.id);
                  }}
                  className="p-1 md:p-0.5 hover:bg-dark-700 rounded"
                >
                  <X className="w-3 h-3 md:w-2.5 md:h-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Reset Values Button */}
        {activeTab === 'values' && onResetValues && (
          <button
            onClick={onResetValues}
            disabled={!canResetValues}
            className={clsx(
              'flex items-center gap-1 px-2 py-1.5 md:px-1.5 md:py-1 text-xs md:text-[10px] border-l border-dark-700 transition-colors',
              canResetValues
                ? 'text-dark-400 hover:text-dark-200 hover:bg-dark-700'
                : 'text-dark-600 cursor-not-allowed'
            )}
            title={canResetValues ? 'Reset to original values' : 'Values match original'}
          >
            <RotateCcw className="w-3.5 h-3.5 md:w-3 md:h-3" />
            <span className="hidden md:inline">Reset</span>
          </button>
        )}
      </div>

      {/* CodeMirror Editor */}
      <div className="flex-1">
        <CodeEditor
          key={activeTab}
          value={currentContent}
          onChange={isReadOnly ? undefined : onEditorChange}
          readOnly={isReadOnly}
          fontSize={isMobile ? 14 : 12}
        />
      </div>

      {/* Errors Panel */}
      {renderResult?.errors && renderResult.errors.length > 0 && (
        <div className="border-t border-dark-700 bg-dark-850 max-h-32 md:max-h-24 overflow-y-auto">
          <div className="p-2 md:p-1.5">
            <h3 className="text-xs md:text-[10px] font-semibold text-accent-error mb-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 md:w-2.5 md:h-2.5" />
              Errors ({renderResult.errors.length})
            </h3>
            <div className="space-y-1 md:space-y-0.5">
              {renderResult.errors.map((error, index) => (
                <div
                  key={index}
                  className="text-xs md:text-[10px] text-dark-300 bg-dark-800 px-2 py-1 md:px-1.5 md:py-0.5 rounded"
                >
                  {error.file && (
                    <span className="text-accent-error">{error.file}: </span>
                  )}
                  {error.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoize to prevent re-renders when parent state changes
export const EditorPanel = memo(EditorPanelComponent);
